import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { Helmet } from '@/pages/home/components/SEOHelmet';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h2>
          <p className="text-foreground-500 mb-6">该漫画可能已下架或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const catName = categories.find((c) => c.slug === comic.category)?.name || '';
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(
      new Date(comic.updatedAt).getTime() - i * 7 * 24 * 60 * 60 * 1000
    ).toISOString().split('T')[0],
  }));

  return (
    <>
      <Helmet
        title={`${comic.title} - 妖精动漫免费在线阅读`}
        description={`妖精动漫免费提供${comic.title}在线阅读，作者${comic.author}，共${comic.chapters}话，${comic.status}。${comic.description.slice(0, 120)}`}
        keywords={`${comic.title},${comic.title}在线看,${comic.title}免费阅读,妖精动漫${comic.title},${comic.tags.join(',')}`}
        canonical={`https://www.yaojingdongman.com/comic/${comic.id}`}
        ogImage={comic.cover}
        ogType="article"
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              {/* Breadcrumb */}
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: catName, href: `/category/${comic.category}` },
                  { label: comic.title },
                ]}
              />

              {/* Detail */}
              <article className="mt-6 flex flex-col md:flex-row gap-8 md:gap-10">
                {/* Cover */}
                <div className="flex-shrink-0">
                  <div className="w-48 h-64 md:w-56 md:h-72 rounded-xl overflow-hidden bg-background-100">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 妖精动漫免费在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    {comic.title}
                  </h1>

                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="flex items-center gap-1 text-sm">
                      <i className="ri-user-line text-foreground-400"></i>
                      <span className="text-foreground-600">{comic.author}</span>
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="flex items-center gap-1 text-sm">
                      <i className="ri-star-fill text-accent-500"></i>
                      <span className="font-semibold text-foreground-700">{comic.rating}</span>
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600">
                      <i className="ri-eye-line mr-1"></i>
                      {(comic.views / 10000).toFixed(0)}万次阅读
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                      comic.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600">共 {comic.chapters} 话</span>
                  </div>

                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100/70 rounded-xl p-4 md:p-5 mb-6">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap">
                      <i className="ri-play-circle-line text-lg"></i>
                      开始阅读 第1话
                    </button>
                    <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap">
                      <i className="ri-bookmark-line"></i>
                      加入收藏
                    </button>
                  </div>
                </div>
              </article>

              {/* Chapter List */}
              <section className="mt-12">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-900">章节列表</h2>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1.5 text-xs font-medium rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap">
                      <i className="ri-sort-asc mr-1"></i>正序
                    </button>
                    <span className="text-xs text-foreground-400">
                      更新至 {chapters[0].num} 话
                    </span>
                  </div>
                </div>

                <div className="bg-background-100/70 rounded-xl p-4 md:p-5">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {chapters.map((ch) => (
                      <a
                        key={ch.num}
                        href={`/comic/${comic.id}/chapter/${ch.num}`}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors group"
                      >
                        <span className="text-sm text-foreground-700 group-hover:text-primary-700 transition-colors truncate">
                          {ch.title}
                        </span>
                        <span className="text-xs text-foreground-400 ml-2 flex-shrink-0 hidden sm:inline">
                          {ch.date}
                        </span>
                      </a>
                    ))}
                  </div>
                  {comic.chapters > 20 && (
                    <button className="mt-4 w-full py-2.5 text-sm font-medium text-primary-600 hover:text-primary-700 bg-background-50 rounded-lg transition-colors">
                      查看全部 {comic.chapters} 话
                      <i className="ri-arrow-down-s-line ml-1"></i>
                    </button>
                  )}
                </div>
              </section>

              {/* Related */}
              <section className="mt-14">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-900">
                    相关推荐
                  </h2>
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                  >
                    更多{catName} <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedComics.map((c) => (
                    <ComicCard
                      key={c.id}
                      id={c.id}
                      title={c.title}
                      author={c.author}
                      cover={c.cover}
                      category={c.category}
                      categoryName={categories.find((cat) => cat.slug === c.category)?.name || ''}
                      rating={c.rating}
                      tags={c.tags}
                      status={c.status}
                      size="sm"
                    />
                  ))}
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "首页",
                    "item": "https://www.yaojingdongman.com"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": catName,
                    "item": `https://www.yaojingdongman.com/category/${comic.category}`
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": comic.title,
                  }
                ]
              },
              {
                "@type": "Article",
                "@id": `https://www.yaojingdongman.com/comic/${comic.id}#article`,
                "headline": comic.title,
                "description": comic.description,
                "author": { "@type": "Person", "name": comic.author },
                "datePublished": "2024-01-01",
                "dateModified": comic.updatedAt,
                "image": comic.cover,
                "inLanguage": "zh-CN",
                "isPartOf": { "@id": "https://www.yaojingdongman.com/#website" }
              }
            ]
          }),
        }}
      />
    </>
  );
}